/*
 * StaleMappingsPage (Feb 2026 v18)
 * -------------------------------------------------------------
 * Data-Ops utility page that scans every AI Mapping's stored
 * `target_companies[]` rationales through the current quality
 * gate, lists the stale ones, and triggers bulk re-runs so the
 * v13/v14/v15/v16 moat can regenerate fresh rationales.
 *
 * Why: after the v17 email-endpoint gate went live, existing
 * mappings with pre-moat rationales (34-word, hallucinated
 * franchise anchors, wrong-archetype) started 422'ing at email
 * generation time. This page is the one-click cleanup that
 * unblocks outreach.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2, Shield, Zap, Stethoscope, ChevronDown, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function StaleMappingsPage() {
  const [scanning, setScanning] = useState(false);
  const [report, setReport] = useState(null);
  const [rerunning, setRerunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, ok: 0, failed: [] });
  const [strictMode, setStrictMode] = useState(false);
  const [strictLoading, setStrictLoading] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnostic, setDiagnostic] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    axios.get(`${API}/api/system-settings/flags`, { withCredentials: true })
      .then(r => setStrictMode(!!r.data?.flags?.strict_ta_gating))
      .catch(() => { /* silent — endpoint may be pre-deploy */ });
  }, []);

  const toggleStrict = async () => {
    const next = !strictMode;
    // Confirm ON — turning it OFF just reverts to soft-gate (safe).
    if (next && !window.confirm(
      'Enable STRICT MODE?\n\nWhen ON, TA/interest-area/specialist-routing send blocks cannot be overridden by any user. "Send anyway" buttons will stop working for these gates.\n\nProceed?',
    )) return;
    setStrictLoading(true);
    try {
      const res = await axios.post(`${API}/api/system-settings/flags`, {
        key: 'strict_ta_gating', value: next,
      }, { withCredentials: true });
      setStrictMode(!!res.data?.flags?.strict_ta_gating);
      toast.success(`Strict TA gating turned ${next ? 'ON' : 'OFF'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to toggle');
    } finally {
      setStrictLoading(false);
    }
  };

  const runScan = async () => {
    setScanning(true);
    try {
      const res = await axios.post(`${API}/api/data-ops/rerun-stale-mappings`, {}, { withCredentials: true, timeout: 180000 });
      setReport(res.data);
      toast.success(`Scan complete — ${res.data.stale_mapping_count} of ${res.data.total_mappings} mappings need re-run`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const rerunAll = async () => {
    if (!report || !report.stale_mappings?.length) return;
    if (!window.confirm(`Re-run ${report.stale_mapping_count} mappings? Each takes ~60–120s and the backend runs at most 2 concurrently. Total ETA: ~${Math.ceil(report.stale_mapping_count / 2 * 1.5)} min.`)) return;
    setRerunning(true);
    setProgress({ done: 0, total: report.stale_mappings.length, ok: 0, failed: [] });
    const queue = [...report.stale_mappings];
    const failed = [];
    let done = 0, ok = 0;
    // Feb 2026 v18 · POLL each mapping to completion before starting
    // the next. Previous version fired 66 requests at once with
    // frontend concurrency 3 — but each request just SPAWNED a
    // background asyncio task on the backend, which piled up
    // waiting on the backend's Semaphore(2). On a memory-constrained
    // pod (tier_0 · 512 MiB), 60+ pending tasks caused an OOM
    // restart that killed EVERY in-flight mapping with "Mapping
    // worker was interrupted (likely a server reload)".
    // Now: concurrency 2 (matches backend semaphore), each worker
    // polls its mapping's `status` until completed/failed before
    // starting the next. Total memory footprint on the backend
    // stays capped at 2 in-flight mappings.
    const pollUntilDone = async (mappingId) => {
      const deadline = Date.now() + 5 * 60 * 1000;  // 5 min hard cap
      while (Date.now() < deadline) {
        try {
          const r = await axios.get(`${API}/api/ai/map-targets/${mappingId}`, { withCredentials: true, timeout: 15000 });
          const status = r?.data?.status;
          if (status === 'completed' || status === 'failed') return status;
        } catch { /* transient — keep polling */ }
        await new Promise(res => setTimeout(res, 5000));  // 5s poll interval
      }
      return 'timeout';
    };
    const worker = async () => {
      while (queue.length > 0) {
        const m = queue.shift();
        if (!m) break;
        try {
          const res = await axios.post(`${API}/api/ai/map-targets`, {
            biotech_company_id: m.biotech_company_id,
            asset_id: m.asset_id,
            force_refresh_discovery: true,
          }, { withCredentials: true, timeout: 30000 });
          const newMappingId = res?.data?.id;
          if (newMappingId) {
            const finalStatus = await pollUntilDone(newMappingId);
            if (finalStatus === 'completed') ok += 1;
            else failed.push({ asset_name: m.asset_name, reason: `Mapping ${finalStatus}` });
          } else {
            failed.push({ asset_name: m.asset_name, reason: 'No mapping id returned' });
          }
        } catch (err) {
          failed.push({ asset_name: m.asset_name, reason: err?.response?.data?.detail || err.message });
        }
        done += 1;
        setProgress({ done, total: report.stale_mappings.length, ok, failed: [...failed] });
      }
    };
    // Concurrency 2 — matches backend `_mapping_semaphore = Semaphore(2)`.
    await Promise.all(Array.from({ length: Math.min(2, queue.length) }, worker));
    setRerunning(false);
    toast.success(`Re-run complete: ${ok} of ${report.stale_mappings.length} succeeded.`, { duration: 15000 });
    // Auto re-scan so the counter drops once server-side state settles.
    setTimeout(runScan, 5000);
  };

  const runDiagnostic = async () => {
    setDiagnosing(true);
    setDiagnostic(null);
    setExpandedRows({});
    try {
      const res = await axios.get(
        `${API}/api/data-ops/diagnostic-stale?limit=200&dry_run_substitute=true`,
        { withCredentials: true, timeout: 300000 },
      );
      setDiagnostic(res.data || null);
      const totalBad = res.data?.totals?.targets_failing_gate || 0;
      const fbStuck = res.data?.fallback_still_failing_count || 0;
      if (totalBad === 0) {
        toast.success('No failing rationales — everything passes the gate. Safe to send outreach.');
      } else if (fbStuck === 0) {
        toast.success(`${totalBad} failing rationales found — Quick Fix will clear all of them.`);
      } else {
        toast.warning(`${totalBad} failing · ${fbStuck} residuals Quick Fix cannot clear — expand rows for reasons.`, { duration: 15000 });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Diagnostic failed');
    } finally {
      setDiagnosing(false);
    }
  };

  const toggleRow = (idx) => setExpandedRows((s) => ({ ...s, [idx]: !s[idx] }));

  const quickFix = async () => {
    if (!window.confirm(
      'Quick Fix: substitutes every failing rationale with a deterministic '
      + 'templated fallback (guaranteed to pass the quality gate). No LLM calls — takes seconds. '
      + 'This is the escape hatch when the re-run loop keeps finding new stale mappings. Proceed?'
    )) return;
    setRerunning(true);
    try {
      const res = await axios.post(
        `${API}/api/data-ops/rerun-stale-mappings/quick-fix`,
        {}, { withCredentials: true, timeout: 300000 },
      );
      const d = res.data || {};
      toast.success(
        `Quick Fix done · substituted ${d.rationales_substituted} rationales across ${d.touched_mappings} mappings. Safe to send outreach.`,
        { duration: 20000 },
      );
      setTimeout(runScan, 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Quick Fix failed');
    } finally {
      setRerunning(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="stale-mappings-page">
      {/* Strict-mode toggle card (v18) */}
      <Card className={strictMode ? 'border-amber-300 bg-amber-50' : ''}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className={`w-5 h-5 ${strictMode ? 'text-amber-700' : 'text-slate-500'}`} />
            Strict TA Gating {strictMode && <span className="text-xs bg-amber-600 text-white px-2 py-0.5 rounded">ON</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-slate-700">
            When <b>ON</b>, TA / interest-area / specialist-routing send blocks are HARD —
            no user can override with &ldquo;Send anyway&rdquo;. Contacts with tagged
            interest_areas absolutely cannot receive off-TA emails.
          </p>
          <div className="flex items-center gap-3 pt-1">
            <Switch checked={strictMode} onCheckedChange={toggleStrict} disabled={strictLoading} data-testid="strict-ta-toggle" />
            <span className="text-sm font-medium">
              Strict mode: {strictMode ? 'ON (overrides disabled)' : 'OFF (soft gate — overrides allowed)'}
            </span>
            {strictLoading && <Loader2 className="w-4 h-4 animate-spin" />}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-indigo-600" />
            Re-run Stale Mappings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            Scans every AI Mapping&apos;s stored rationales through the current
            quality gate (franchise-anchor mismatch, cross-TA mismatch,
            wrong-archetype, unverified product, weak specificity). Mappings
            with any failing rationale are re-run so the moat can regenerate
            fresh, gate-passing rationales. This is the fix for outreach
            emails being 422&apos;d with &ldquo;Rationale quality gate rejected&rdquo;.
          </p>
          <div className="flex gap-3 flex-wrap">
            <Button onClick={runScan} disabled={scanning || rerunning || diagnosing} data-testid="stale-scan-btn">
              {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
              Scan for Stale Mappings
            </Button>
            <Button
              onClick={runDiagnostic}
              disabled={diagnosing || scanning || rerunning}
              variant="outline"
              className="border-sky-300 text-sky-800 hover:bg-sky-50"
              data-testid="stale-diagnostic-btn"
              title="Read-only diagnostic — dumps exact failing rationales + shows which residuals Quick Fix cannot clear. Use this when Quick Fix leaves residuals."
            >
              {diagnosing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Stethoscope className="w-4 h-4 mr-2" />}
              Diagnose Residuals
            </Button>
            {report && report.stale_mapping_count > 0 && (
              <Button
                onClick={rerunAll}
                disabled={rerunning || scanning}
                className="bg-indigo-600 text-white hover:bg-indigo-700"
                data-testid="stale-rerun-all-btn"
              >
                {rerunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Re-run All {report.stale_mapping_count} Stale Mappings
              </Button>
            )}
            {report && report.stale_mapping_count > 0 && (
              <Button
                onClick={quickFix}
                disabled={rerunning || scanning}
                className="bg-emerald-600 text-white hover:bg-emerald-700"
                data-testid="stale-quick-fix-btn"
                title="Deterministic Python-only fix — substitutes failing rationales with a templated fallback that always passes the gate. No LLM. Takes seconds."
              >
                {rerunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                Quick Fix (no LLM · seconds)
              </Button>
            )}
          </div>
          {report && (
            <div className="grid grid-cols-4 gap-3 mt-4">
              <StatTile label="Mappings scanned" value={report.total_mappings} />
              <StatTile label="Targets scanned" value={report.total_targets} />
              <StatTile label="Bad rationales" value={report.total_bad_targets} tone="warn" />
              <StatTile label="Mappings need re-run" value={report.stale_mapping_count} tone={report.stale_mapping_count ? 'warn' : 'ok'} />
            </div>
          )}
          {rerunning && (
            <div className="p-3 border border-indigo-200 bg-indigo-50 rounded">
              <div className="text-sm font-medium text-indigo-900">
                Re-running {progress.done} of {progress.total} — {progress.ok} succeeded
              </div>
              <div className="mt-2 w-full bg-white rounded h-2 overflow-hidden border border-indigo-200">
                <div className="h-full bg-indigo-500 transition-all" style={{ width: `${progress.total ? (progress.done * 100 / progress.total) : 0}%` }} />
              </div>
              {progress.failed.length > 0 && (
                <div className="mt-2 text-xs text-rose-700">
                  {progress.failed.length} failed: {progress.failed.slice(0, 3).map(f => f.asset_name).join(', ')}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {report && report.stale_mappings?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Stale Mappings ({report.stale_mappings.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {report.stale_mappings.map((m) => (
                <div key={m.mapping_id} className="border border-slate-200 rounded p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{m.asset_name || m.asset_id}</div>
                    <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded">
                      {m.bad_target_count} of {m.total_target_count} bad
                    </div>
                  </div>
                  {m.sample_reasons?.length > 0 && (
                    <div className="mt-2 text-[11px] text-slate-600 space-y-1">
                      {m.sample_reasons.map((r, i) => (
                        <div key={i}>
                          <span className="font-semibold">{r.company_name}:</span> {r.reason || r.quality}
                        </div>
                      ))}
                      {m.bad_target_count > m.sample_reasons.length && (
                        <div className="text-slate-400">+{m.bad_target_count - m.sample_reasons.length} more…</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {diagnostic && (
        <Card className="border-sky-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Stethoscope className="w-5 h-5 text-sky-700" />
              Diagnostic Report
              {diagnostic.totals?.targets_failing_gate === 0 && (
                <span className="text-xs bg-emerald-600 text-white px-2 py-0.5 rounded ml-2">All clear</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-4 gap-3">
              <StatTile label="Mappings scanned" value={diagnostic.totals?.mappings_scanned || 0} />
              <StatTile label="Targets scanned" value={diagnostic.totals?.targets_scanned || 0} />
              <StatTile
                label="Targets failing gate"
                value={diagnostic.totals?.targets_failing_gate || 0}
                tone={diagnostic.totals?.targets_failing_gate ? 'warn' : 'ok'}
              />
              <StatTile
                label="Fallback also fails"
                value={diagnostic.fallback_still_failing_count ?? 0}
                tone={diagnostic.fallback_still_failing_count ? 'warn' : 'ok'}
              />
            </div>
            {diagnostic.truncated_scan && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                ⚠️ Scan stopped early after hitting the {diagnostic.failures_limit}-row sample cap.
                Totals above reflect the partial scan only — the true bad-target count on production
                may be higher. Fix the residual gate rules using the samples below, re-run Quick Fix,
                then run diagnostic again to confirm 0 residuals.
              </div>
            )}

            {diagnostic.quality_histogram && Object.keys(diagnostic.quality_histogram).length > 0 && (
              <div className="border rounded p-3 bg-slate-50">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">
                  Failure reasons (histogram)
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(diagnostic.quality_histogram).map(([q, n]) => (
                    <div key={q} className="text-xs bg-white border border-slate-200 rounded px-2 py-1">
                      <span className="font-semibold text-slate-800">{q}</span>: <span className="text-rose-700">{n}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {diagnostic.fallback_still_failing_count > 0 && (
              <div className="border border-rose-300 bg-rose-50 rounded p-3">
                <div className="text-sm font-semibold text-rose-900 mb-1">
                  🔴 {diagnostic.fallback_still_failing_count} residuals Quick Fix CANNOT clear
                </div>
                <div className="text-xs text-rose-800">
                  These are rationales where even the deterministic templated fallback fails the gate.
                  Fix the gate rule that trips these (see reasons in the rows below), then re-run Quick Fix.
                </div>
                {diagnostic.fallback_still_failing_histogram && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {Object.entries(diagnostic.fallback_still_failing_histogram).map(([q, n]) => (
                      <div key={q} className="text-xs bg-white border border-rose-200 rounded px-2 py-1">
                        <span className="font-semibold text-rose-900">{q}</span>: <span className="text-rose-700">{n}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {diagnostic.failures?.length > 0 && (
              <div>
                <div className="text-sm font-semibold text-slate-800 mb-2">
                  Failing rationales ({diagnostic.failures_returned} of up to {diagnostic.failures_limit})
                </div>
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {diagnostic.failures.map((f, idx) => {
                    const open = !!expandedRows[idx];
                    const fallbackFails = f.fallback_quality && f.fallback_quality !== 'strong';
                    return (
                      <div
                        key={idx}
                        className={`border rounded text-sm ${fallbackFails ? 'border-rose-300 bg-rose-50/30' : 'border-slate-200'}`}
                      >
                        <button
                          type="button"
                          onClick={() => toggleRow(idx)}
                          className="w-full flex items-start gap-2 p-3 text-left hover:bg-slate-50"
                          data-testid={`diagnostic-row-${idx}`}
                        >
                          {open ? <ChevronDown className="w-4 h-4 mt-0.5 flex-none" /> : <ChevronRight className="w-4 h-4 mt-0.5 flex-none" />}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold">{f.target_company_name || '(no name)'}</span>
                              <span className="text-[11px] bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded">
                                {f.asset_name || f.asset_id}
                              </span>
                              <span className="text-[11px] bg-amber-100 border border-amber-200 text-amber-900 px-1.5 py-0.5 rounded">
                                {f.quality}
                              </span>
                              {fallbackFails && (
                                <span className="text-[11px] bg-rose-600 text-white px-1.5 py-0.5 rounded font-semibold">
                                  fallback: {f.fallback_quality}
                                </span>
                              )}
                              {f.target_source && (
                                <span className="text-[11px] text-slate-500">{f.target_source}</span>
                              )}
                            </div>
                            <div className="text-xs text-slate-700 mt-0.5 truncate">
                              {f.reason}
                            </div>
                          </div>
                        </button>
                        {open && (
                          <div className="px-3 pb-3 space-y-2 border-t border-slate-100">
                            <DetailRow label="TA / Indication" value={`${f.asset_ta || '—'} · ${f.asset_indication || '—'}`} />
                            <DetailRow label="Has company_profile" value={f.has_company_profile ? 'yes' : 'no'} />
                            <DetailRow label="Archetype" value={f.target_archetype || '—'} />
                            <DetailRow label="Franchise anchor TAs" value={(f.target_franchise_anchors_keys || []).join(', ') || '—'} />
                            <DetailRow label="Word count" value={String(f.rationale_word_count || 0)} />
                            <DetailRow label="Extracted brand tokens" value={(f.extracted_brand_tokens || []).join(', ') || '(none)'} />
                            {f.rationale_source_flag && (
                              <DetailRow label="Rationale source" value={f.rationale_source_flag} />
                            )}
                            {f.quick_fix_applied_at && (
                              <DetailRow label="Last Quick Fix at" value={f.quick_fix_applied_at} />
                            )}
                            <div>
                              <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Current rationale</div>
                              <div className="text-xs bg-white border border-slate-200 rounded p-2 whitespace-pre-wrap">
                                {f.rationale || '(empty)'}
                              </div>
                            </div>
                            {f.fallback_rationale && (
                              <div>
                                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                                  Templated fallback dry-run
                                  {fallbackFails ? (
                                    <span className="ml-1 text-rose-700 font-semibold">— STILL FAILS ({f.fallback_quality})</span>
                                  ) : (
                                    <span className="ml-1 text-emerald-700 font-semibold">— would pass</span>
                                  )}
                                </div>
                                <div className={`text-xs border rounded p-2 whitespace-pre-wrap ${fallbackFails ? 'bg-rose-50 border-rose-200' : 'bg-emerald-50 border-emerald-200'}`}>
                                  {f.fallback_rationale}
                                </div>
                                {fallbackFails && f.fallback_reason && (
                                  <div className="text-xs text-rose-800 mt-1">
                                    <span className="font-semibold">Reason:</span> {f.fallback_reason}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {diagnostic.hint && (
              <div className="text-xs text-slate-500 italic">💡 {diagnostic.hint}</div>
            )}
          </CardContent>
        </Card>
      )}

      {report && report.stale_mapping_count === 0 && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4 flex items-center gap-3 text-emerald-800">
            <CheckCircle2 className="w-5 h-5" /> All mappings pass the current quality gate — no re-run needed.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatTile({ label, value, tone = 'default' }) {
  const toneCls = tone === 'warn'
    ? 'text-rose-700 bg-rose-50 border-rose-200'
    : tone === 'ok'
    ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : 'text-slate-800 bg-slate-50 border-slate-200';
  return (
    <div className={`border rounded p-3 ${toneCls}`}>
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-[11px] uppercase tracking-wide opacity-80">{label}</div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-start gap-2 text-xs">
      <div className="w-40 flex-none text-slate-500 uppercase tracking-wide">{label}</div>
      <div className="flex-1 text-slate-800 break-words">{value}</div>
    </div>
  );
}

